import { type EventType, type TsTypeType } from '../../types.js'
import type { NameAndType, TsCodeType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { BoVisitor } from '../../bos/bo-visitor.js'
import { TrackByFunctionNames, TrackByFunctions } from '../logic/for-block.js'
import { Block } from '../index.js'
import { DataTableColumnBlock } from './data-table-column-block.js'
import { Parameter } from '../../script/parameter.js'
import { BoReference } from '../../bos/bo-reference.js'
import { PagedBlockDataSourceDefinition } from '../common/block-data-source-definition.js'
import { BlockWithDataSource } from '../common/block-with-data-source.js'
import { Expression } from '../../data/text-or-code.js'
import { Screen } from '../../bos/screen.js'
import { type ScreenSize, ScreenSizes } from '../../consts.js'

@jsonClass('DataTableBlock')
export class DataTableBlock extends Block implements BlockWithDataSource {
	@jsonProperty(PagedBlockDataSourceDefinition)
	@ScreenEditorInput<DataTableBlock>({
		order: 1,
		label: '',
		compositionObj: (blocks) => blocks[0].dataSource,
	})
	dataSource: PagedBlockDataSourceDefinition =
		new PagedBlockDataSourceDefinition()

	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: 'Additional header rows',
		tooltip: () => ({
			text: `To display additional rows before the table content, enter e.g. [['subHeader1', true], ['subHeader2', false]] here (true=sticky, false=non-sticky) and add ContentAreas with corresponding names to each relevant column`,
		}),
		codeLanguage: 'ts',
		tsReturnType: () => '[string, boolean][]',
		editorSize: 'singleline',
		isBinding: false,
	})
	@jsonProperty(Expression)
	additionalHeaderRows = new Expression('[]')

	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: 'Additional footer rows',
		tooltip: () => ({
			text: `To display additional rows after the table content, enter e.g. [['footer1', false], ['footer2', true]] here (true=sticky, false=non-sticky) and add ContentAreas with corresponding names to each relevant column`,
		}),
		codeLanguage: 'ts',
		tsReturnType: () => '[string, boolean][]',
		editorSize: 'singleline',
		isBinding: false,
	})
	@jsonProperty(Expression)
	additionalFooterRows = new Expression('[]')

	@jsonProperty()
	@ScreenEditorInput<DataTableBlock>({
		inputType: 'checkbox',
		order: 5,
		tab: 'Advanced',
		label: 'Is configurable',
	})
	isConfigurable: boolean = true

	@jsonProperty(BoReference)
	@ScreenEditorInput<DataTableBlock>({
		inputType: 'boRef',
		order: 6,
		tab: 'Advanced',
		label: 'Save/load configuration at',
		allowedBoTypes: ['UserDefinedConfiguration'],
		includeImportedModules: true,
		isVisible: (blocks) => blocks[0].isConfigurable,
	})
	userDefinedConfigurationRef: BoReference = new BoReference()

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 7,
		tab: 'Advanced',
		label: 'Vertical if screen is smaller than',
		tooltip: () => ({ text: 'Use XSmall if you never want to go vertical' }),
		options: ScreenSizes.map((s) => [s, s]),
	})
	verticalIfScreenSmallerThan: ScreenSize = 'XSmall'

	@jsonProperty()
	@ScreenEditorInput<DataTableBlock>({
		inputType: 'checkbox',
		order: 5,
		tab: 'Advanced',
		label: 'Sticky header',
	})
	isHeaderSticky: boolean = true

	@jsonProperty()
	@ScreenEditorInput<DataTableBlock>({
		inputType: 'checkbox',
		order: 7,
		tab: 'Data Management',
		label: 'Use global filter',
	})
	useGlobalFilter: boolean = true

	@jsonProperty(Expression)
	@ScreenEditorInput<DataTableBlock>({
		inputType: 'code',
		order: 8,
		label: 'Global filter binding',
		tab: 'Data Management',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: true,
		isVisible: (blocks) => blocks[0].useGlobalFilter,
	})
	globalFilterBindingCode: Expression = new Expression('')

	@jsonProperty(String)
	@ScreenEditorInput<DataTableBlock>({
		inputType: 'code',
		order: 9,
		label: 'Debouncing delay (ms)',
		tab: 'Data Management',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isVisible: (blocks) => blocks[0].dataSource.sourceType == 'entity',
		isMandatory: () => true,
		tooltip: () => ({
			text: 'Delay in milliseconds that the app will wait after keyboard inputs until the search is executed; this helps avoid sending a request after every single typed character',
		}),
	})
	debouncingDelayCode?: TsCodeType = '250'

	canAcceptVariableString = (variable: NameAndType) => !!variable.isArray
	acceptVariableString = (variable: NameAndType, screen: Screen) => {
		this.dataSource.acceptVariableString(variable, screen)
	}

	constructor(init: Partial<DataTableBlock> = {}) {
		super()
		this.init(init)
	}

	static createNewForEditor(): DataTableBlock {
		return new this({
			children: [DataTableColumnBlock.createNewForEditor()],
		})
	}

	getEditorTitle() {
		return `${super.getEditorTitle()}`
	}

	canHaveChild(child: Block): boolean {
		return child instanceof DataTableColumnBlock
	}

	getPreviewDirection(): 'column' | 'row' {
		return 'row'
	}

	get allowedEventTypes(): EventType[] {
		const rowClickEvent: EventType = {
			name: 'rowClick',
			parameters: [
				new Parameter({
					name: this.dataSource.loopVariableDefinition,
					type: this.dataSource.getItemTsType(),
					defaultExpression: '$event[1]',
				}),
			],
		}

		if (this.dataSource.indexVariableDefinition) {
			if (!rowClickEvent.parameters) rowClickEvent.parameters = []
			rowClickEvent.parameters.push(
				new Parameter({
					name: this.dataSource.indexVariableDefinition,
					type: 'number',
					defaultExpression: '$event[2]',
				}),
			)
		}

		return [
			rowClickEvent,
			{
				name: 'entityDataLoaded',
				parameters: [
					new Parameter({
						name: 'data',
						type:
							this.dataSource.sourceType == 'entity'
								? `${this.dataSource.getItemTsType()}[]`
								: 'never',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'filterChange',
				parameters: [
					new Parameter({
						name: 'filter',
						type: 'string',
						defaultExpression: '$event',
					}),
				],
			},
		]
	}

	get allowedStylePrefixes(): string[] {
		return ['row']
	}

	public getOwnVariables(
		includeDeclarationVariables: boolean,
	): Record<string, string | null> {
		const varDefs = {
			[this.dataSource.loopVariableDefinition]: this.dataSource.getItemTsType(),
		}
		varDefs[this.dataSource.getIndexVariableName()] = 'number'
		return varDefs
	}

	protected visitThisWithBoVisitor(
		visitor: BoVisitor,
		pathPrefix: (string | number)[],
	): void {
		// this.visitCodePropertyWithBoVisitor(visitor, pathPrefix, DataTableBlock, 'collectionCode')
		// this.visitCodePropertyWithBoVisitor(visitor, pathPrefix, DataTableBlock, 'loopVariableType')
	}

	getEditorCategory(): string {
		return 'Tables & Lists'
	}
}
